/**
 * The operator's working scope: the namespace the console is acting on.
 *
 * 🔑 By default it is the namespace in the token. A parent operator may pick
 * one of its providers (a strict descendant) and the console then acts on
 * that namespace -- every report, every screen. That is not the UI choosing a
 * tenant: the gateway already lets a token act on any descendant of its own
 * namespace (radaccess NamespaceIsolationMiddleware, dot boundary), so the
 * choice is bounded by what the token could do anyway, and a namespace
 * outside that bound is refused HERE before any request is sent.
 */

/** `ns` is `root` itself or strictly beneath it, on a dot boundary. */
export function isWithin(ns: string, root: string): boolean {
  return ns === root || ns.startsWith(root + ".");
}

/** `ns` is strictly beneath `root`: a provider of it. */
export function isProviderOf(ns: string, root: string): boolean {
  return ns !== root && ns.startsWith(root + ".");
}

/**
 * The scope a stored selection resolves to. A selection that is not a
 * provider of the token's namespace -- stale, tampered, or from another login
 * -- resolves to the root, never to itself.
 */
export function resolveScope(root: string | null, selected: string | null): string | null {
  if (!root) return null;
  if (selected && isProviderOf(selected, root)) return selected;
  return root;
}

/** A human name for a namespace when no legal name is known: the last segment. */
export function shortName(ns: string): string {
  const last = ns.split(".").filter(Boolean).pop() ?? ns;
  return last.charAt(0).toUpperCase() + last.slice(1);
}

export const SCOPE_KEY_PREFIX = "ankhos.scope:";
